
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Crown, Trophy, TrendingUp, Award, Calendar,
  ChevronDown, ChevronUp, Filter, Star, Zap,
  Target, Activity, BarChart3, Users, Medal, X,
  TrendingDown, ArrowUp, ArrowDown, Minus, User
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Import components
import LegacyScoreFactors from './LegacyScoreFactors';
import LegacyScoreOverTime from './LegacyScoreOverTime';
import LegacyScoreRankOverTime from './LegacyScoreRankOverTime';
import IndividualTeamsLegacyChart2 from './IndividualTeamsLegacyChart2';
import LegacyScoreBanner from './LegacyScoreBanner';

// The league's first season. The latest season comes from the data itself so the
// range filter picks up each new year without a code change.
const FIRST_SEASON = 2012;

const LegacyScore = ({ h2hData = [], finalData = [], legacyData: legacyByYearData = [], loading = false, error = null }) => {
  const [yearRange, setYearRange] = useState([FIRST_SEASON, FIRST_SEASON]);
  // Include h2hData so an in-progress season shows up as soon as a week is played.
  // Its weekly all-play and win/loss points accrue immediately; the final-standings
  // bonus stays out until Final Data exists, which is exactly the right behaviour.
  const latestSeason = useMemo(() => {
    const years = [...h2hData, ...finalData, ...legacyByYearData]
      .map((row) => Number(row?.Year))
      .filter((y) => Number.isFinite(y));
    return years.length ? Math.max(...years) : FIRST_SEASON;
  }, [h2hData, finalData, legacyByYearData]);
  // A season only has Final Data once it's over, so anything past that is in progress.
  const finalSeason = useMemo(() => {
    const years = finalData.map((r) => Number(r?.Year)).filter((y) => Number.isFinite(y));
    return years.length ? Math.max(...years) : FIRST_SEASON;
  }, [finalData]);
  // Open on the full history once data lands, but leave the user's own range alone.
  const rangeTouched = useRef(false);
  useEffect(() => {
    if (!rangeTouched.current && latestSeason > FIRST_SEASON) {
      setYearRange([FIRST_SEASON, latestSeason]);
      rangeTouched.current = true;
    }
  }, [latestSeason]);
  const setRange = (range) => {
    rangeTouched.current = true;
    setYearRange(range);
  };
  const [selectedManager, setSelectedManager] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [legacyData, setLegacyData] = useState([]);
  const [individualBreakdown, setIndividualBreakdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedManager, setExpandedManager] = useState(null);

  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to get manager image
  const getManagerImage = (manager) => {
    const mainImage = `/${manager.toLowerCase()}main.png`;
    const fallbackImage = `/${manager.toLowerCase()}.png`;
    
    return {
      main: mainImage,
      fallback: fallbackImage
    };
  };

  // Inject range slider styles
  React.useEffect(() => {
    if (!document.getElementById('legacy-slider-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'legacy-slider-styles';
      styleSheet.textContent = `
        .range-slider-container {
          position: relative;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          margin: 10px 0;
        }
        
        .range-slider-track {
          position: absolute;
          height: 8px;
          background: linear-gradient(to right, #8b5cf6, #a855f7);
          border-radius: 4px;
          top: 0;
        }
        
        .range-slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
          position: absolute;
          top: -6px;
          height: 20px;
          width: 100%;
          outline: none;
        }
        
        .range-slider::-webkit-slider-track {
          background: transparent;
          height: 20px;
        }
        
        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          border: 3px solid white;
          transition: all 0.2s ease;
        }
        
        .range-slider::-webkit-slider-thumb:hover {
          background: #7c3aed;
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        
        .range-slider::-moz-range-track {
          background: transparent;
          height: 20px;
          border: none;
        }
        
        .range-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: all 0.2s ease;
        }
        
        .range-slider::-moz-range-thumb:hover {
          background: #7c3aed;
          transform: scale(1.1);
        }
        
        .range-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }
        
        .range-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // FIXED: Weekly ranking calculation that exactly matches LegacyScoreFactors
  const calculateWeeklyPositions = (regSeasonGames, manager = '') => {
    // Use the EXACT same field names as LegacyScoreFactors for consistency
    const weeklyPositions = {
      first: 0, twelfth: 0, second: 0, eleventh: 0, third: 0, tenth: 0,
      fourth: 0, ninth: 0, fifth: 0, eighth: 0, sixth: 0, seventh: 0
    };

    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      const lossesThisWeek = game['Losses (This Week)'] || 0;
      
      // IMPORTANT: Validate data integrity
      if (winsThisWeek + lossesThisWeek !== 11) {
        console.warn('Invalid weekly ranking data for', manager, ':', {
          year: game.Year,
          week: game.Week,
          manager: game.Manager1,
          wins: winsThisWeek,
          losses: lossesThisWeek,
          total: winsThisWeek + lossesThisWeek
        });
        return; // Skip invalid records
      }
      
      // FIXED: Weekly position logic - count ALL instances, not just wins/losses
      // This EXACTLY matches the logic in LegacyScoreFactors.js
      
      // 1st place: Beat 11 teams (11 wins, 0 losses)
      if (winsThisWeek === 11) weeklyPositions.first += 1;
      
      // 12th place: Beat 0 teams (0 wins, 11 losses)  
      if (winsThisWeek === 0) weeklyPositions.twelfth += 1;
      
      // 2nd place: Beat 10 teams (10 wins, 1 loss)
      if (winsThisWeek === 10) weeklyPositions.second += 1;
      
      // 11th place: Beat 1 team (1 win, 10 losses)
      if (winsThisWeek === 1) weeklyPositions.eleventh += 1;
      
      // 3rd place: Beat 9 teams (9 wins, 2 losses)
      if (winsThisWeek === 9) weeklyPositions.third += 1;
      
      // 10th place: Beat 2 teams (2 wins, 9 losses)
      if (winsThisWeek === 2) weeklyPositions.tenth += 1;
      
      // 4th place: Beat 8 teams (8 wins, 3 losses)
      if (winsThisWeek === 8) weeklyPositions.fourth += 1;
      
      // 9th place: Beat 3 teams (3 wins, 8 losses)
      if (winsThisWeek === 3) weeklyPositions.ninth += 1;
      
      // 5th place: Beat 7 teams (7 wins, 4 losses)
      if (winsThisWeek === 7) weeklyPositions.fifth += 1;
      
      // 8th place: Beat 4 teams (4 wins, 7 losses)  
      if (winsThisWeek === 4) weeklyPositions.eighth += 1;
      
      // 6th place: Beat 6 teams (6 wins, 5 losses)
      if (winsThisWeek === 6) weeklyPositions.sixth += 1;
      
      // 7th place: Beat 5 teams (5 wins, 6 losses)
      if (winsThisWeek === 5) weeklyPositions.seventh += 1;
    });

    return weeklyPositions;
  };

  // Calculate Legacy Scores based on the corrected algorithm - EXACTLY matching LegacyScoreFactors
  const calculateLegacyScores = () => {
    if (!h2hData.length || !finalData.length) return [];

    const legacyScores = [];

    // Find the most recent week played across all data
    const allWeeks = h2hData.map(game => ({
      year: game.Year,
      week: game.Week,
      leagueWeek: game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week) // Calculate if not provided
    }));
    
    const mostRecentWeek = Math.max(...allWeeks.map(w => w.leagueWeek));
    const previousWeek = mostRecentWeek - 1;

    console.log('=== WEEK CALCULATION DEBUG ===');
    console.log('All weeks found:', allWeeks.slice(-10)); // Show last 10 weeks
    console.log('Most recent week:', mostRecentWeek);
    console.log('Previous week:', previousWeek);
    console.log('Number of games in most recent week:', h2hData.filter(g => 
      (g.LeagueWeek || ((g.Year - 2012) * 17 + g.Week)) === mostRecentWeek
    ).length);
    console.log('Number of games in previous week:', h2hData.filter(g => 
      (g.LeagueWeek || ((g.Year - 2012) * 17 + g.Week)) === previousWeek
    ).length);

    // For each manager, calculate their legacy score components - MATCH LegacyScoreFactors EXACTLY
    managers.forEach(manager => {
      // Helper function to calculate legacy score up to a specific week
      const calculateScoreUpToWeek = (maxLeagueWeek) => {
        // CRITICAL: Filter data exactly like LegacyScoreFactors
        const managerH2H = h2hData.filter(game => 
          game.Manager1 === manager && 
          game.Year >= yearRange[0] && 
          game.Year <= yearRange[1]
        );

        const managerSeasons = finalData.filter(season => 
          season.Owner === manager &&
          season.Year >= yearRange[0] && 
          season.Year <= yearRange[1]
        );

        const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
        const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
        const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

        // FIXED: Use the corrected weekly position calculation
        const weeklyPositions = calculateWeeklyPositions(regSeasonGames, manager);

        const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
        const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

        // FIXED: Standings finishes - use array indexing like LegacyScoreFactors
        const standingsFinishes = Array.from({length: 12}, () => 0); // 0-indexed for easier mapping

        // IMPORTANT: Don't filter by completed seasons for main calculation - use ALL seasons like LegacyScoreFactors
        managerSeasons.forEach(season => {
          if (season.Standings >= 1 && season.Standings <= 12) {
            standingsFinishes[season.Standings - 1]++; // Convert to 0-based indexing
          }
        });

        // Calculate scoring champion and loser for ALL seasons - MATCH LegacyScoreFactors EXACTLY
        let scoreChamp = 0;
        let scoreLoser = 0;
        
        // For each season in the range, find who had highest/lowest PF
        for (let year = yearRange[0]; year <= yearRange[1]; year++) {
          const seasonData = finalData.filter(season => season.Year === year);
          
          if (seasonData.length > 0) {
            // Find highest and lowest PF (Points For) in this season
            const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
            const minPF = Math.min(...seasonData.map(s => s.PF || 0));
            
            // Check if this manager had the highest PF
            const managerSeason = seasonData.find(s => s.Owner === manager);
            if (managerSeason && managerSeason.PF === maxPF) {
              scoreChamp++;
            }
            
            // Check if this manager had the lowest PF
            if (managerSeason && managerSeason.PF === minPF) {
              scoreLoser++;
            }
          }
        }

        // FIXED: Calculate standings score exactly like LegacyScoreFactors
        const standingsScore = 
          (standingsFinishes[0] * 125) +   // 1st place
          (standingsFinishes[1] * 75) +    // 2nd place
          (standingsFinishes[2] * 60) +    // 3rd place
          (standingsFinishes[3] * 40) +    // 4th place
          (standingsFinishes[4] * 30) +    // 5th place
          (standingsFinishes[5] * 15) +    // 6th place
          (standingsFinishes[6] * -15) +   // 7th place
          (standingsFinishes[7] * -30) +   // 8th place
          (standingsFinishes[8] * -40) +   // 9th place
          (standingsFinishes[9] * -50) +   // 10th place
          (standingsFinishes[10] * -60) +  // 11th place
          (standingsFinishes[11] * -80);   // 12th place

        // FIXED: Complete legacy score calculation exactly matching LegacyScoreFactors
        return regWins * 5 + regLosses * -5 +
               weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +
               weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +
               weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +
               weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +
               weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +
               weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +
               playoffWins * 10 +
               standingsScore +
               scoreChamp * 30 + scoreLoser * -30;
      };

      // BETTER APPROACH: Calculate what actually happened in the most recent week
      const calculateRecentWeekChange = () => {
        let totalChange = 0;
        const changeDetails = [];

        // Find games from the most recent week for this manager
        const mostRecentWeekGames = h2hData.filter(game => 
          game.Manager1 === manager && 
          (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek
        );

        // 1. REGULAR SEASON WINS/LOSSES from this week
        const regSeasonGames = mostRecentWeekGames.filter(game => game.Type === 'Regular Season');
        regSeasonGames.forEach(game => {
          if (game.IsWin) {
            totalChange += 5;
            changeDetails.push('Regular season win: +5');
          }
          if (game.IsLoss) {
            totalChange -= 5;
            changeDetails.push('Regular season loss: -5');
          }
        });

        // 2. WEEKLY SCORING POSITIONS from this week
        regSeasonGames.forEach(game => {
          const winsThisWeek = game['Wins (This Week)'] || 0;
          
          // Calculate weekly scoring points for this specific game/week
          if (winsThisWeek === 11) { totalChange += 15; changeDetails.push('1st place weekly: +15'); }
          else if (winsThisWeek === 10) { totalChange += 5; changeDetails.push('2nd place weekly: +5'); }
          else if (winsThisWeek === 9) { totalChange += 4; changeDetails.push('3rd place weekly: +4'); }
          else if (winsThisWeek === 8) { totalChange += 3; changeDetails.push('4th place weekly: +3'); }
          else if (winsThisWeek === 7) { totalChange += 2; changeDetails.push('5th place weekly: +2'); }
          else if (winsThisWeek === 6) { totalChange += 1; changeDetails.push('6th place weekly: +1'); }
          else if (winsThisWeek === 5) { totalChange -= 1; changeDetails.push('7th place weekly: -1'); }
          else if (winsThisWeek === 4) { totalChange -= 2; changeDetails.push('8th place weekly: -2'); }
          else if (winsThisWeek === 3) { totalChange -= 3; changeDetails.push('9th place weekly: -3'); }
          else if (winsThisWeek === 2) { totalChange -= 4; changeDetails.push('10th place weekly: -4'); }
          else if (winsThisWeek === 1) { totalChange -= 5; changeDetails.push('11th place weekly: -5'); }
          else if (winsThisWeek === 0) { totalChange -= 15; changeDetails.push('12th place weekly: -15'); }
        });

        // 3. PLAYOFF WINS from this week
        const playoffGames = mostRecentWeekGames.filter(game => game.Type === 'Playoffs');
        playoffGames.forEach(game => {
          if (game.IsWin) {
            totalChange += 10;
            changeDetails.push('Playoff win: +10');
          }
        });

        // 4. END OF SEASON FACTORS (if this is Week 17) - ALWAYS APPLIES TO ALL MANAGERS
        const isSeasonEnd = mostRecentWeekGames.some(game => game.Week === 17) || 
                           h2hData.some(game => 
                             (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek && 
                             game.Week === 17
                           );
        
        if (isSeasonEnd) {
          // Find what season just ended - look at any Week 17 game in the most recent week
          const endedSeasonYear = h2hData.find(game => 
            (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek && 
            game.Week === 17
          )?.Year;
          
          if (endedSeasonYear) {
            // FINAL STANDINGS BONUS/PENALTY - EVERY manager gets this
            const managerSeason = finalData.find(season => 
              season.Owner === manager && season.Year === endedSeasonYear
            );
            
            if (managerSeason && managerSeason.Standings >= 1 && managerSeason.Standings <= 12) {
              const standingsPoints = [125, 75, 60, 40, 30, 15, -15, -30, -40, -50, -60, -80][managerSeason.Standings - 1];
              totalChange += standingsPoints;
              changeDetails.push(`${managerSeason.Standings}${getOrdinalSuffix(managerSeason.Standings)} place finish: ${standingsPoints > 0 ? '+' : ''}${standingsPoints}`);
            }

            // SCORING CHAMPION/LOSER BONUS/PENALTY
            const seasonData = finalData.filter(season => season.Year === endedSeasonYear);
            if (seasonData.length > 0) {
              const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
              const minPF = Math.min(...seasonData.map(s => s.PF || 0));
              
              const managerPF = managerSeason?.PF || 0;
              if (managerPF === maxPF) {
                totalChange += 30;
                changeDetails.push('Scoring champion: +30');
              }
              if (managerPF === minPF) {
                totalChange -= 30;
                changeDetails.push('Scoring loser: -30');
              }
            }
          }
        }

        // Debug logging
        if (manager === 'Kevin' || manager === 'Nipples') {
          console.log(`=== ${manager} RECENT WEEK CHANGE CALCULATION ===`);
          console.log('Most recent week games:', mostRecentWeekGames.length);
          console.log('Is season end (Week 17)?', isSeasonEnd);
          console.log('Change details:', changeDetails);
          console.log('Total calculated change:', totalChange);
        }

        return totalChange;
      };

      // Helper function for ordinal suffixes
      const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return num + 'st';
        if (j === 2 && k !== 12) return num + 'nd';
        if (j === 3 && k !== 13) return num + 'rd';
        return num + 'th';
      };

      // Calculate the actual change from recent week events
      const scoreChange = calculateRecentWeekChange();

      // Get the detailed breakdown for current week - EXACTLY like LegacyScoreFactors
      const managerH2H = h2hData.filter(game => 
        game.Manager1 === manager && 
        game.Year >= yearRange[0] && 
        game.Year <= yearRange[1]
      );

      const managerSeasons = finalData.filter(season => 
        season.Owner === manager &&
        season.Year >= yearRange[0] && 
        season.Year <= yearRange[1]
      );

      const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
      const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
      const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);
      const weeklyPositions = calculateWeeklyPositions(regSeasonGames, manager);
      const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
      const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

      // Calculate standings finishes - FIXED to match LegacyScoreFactors exactly
      const standingsFinishes = Array.from({length: 12}, () => 0); // 0-indexed array

      managerSeasons.forEach(season => {
        if (season.Standings >= 1 && season.Standings <= 12) {
          standingsFinishes[season.Standings - 1]++; // Convert to 0-based indexing
        }
      });

      // Calculate scoring champion/loser - FIXED to match LegacyScoreFactors exactly
      let scoreChamp = 0;
      let scoreLoser = 0;
      
      // For each season in the range, find who had highest/lowest PF
      for (let year = yearRange[0]; year <= yearRange[1]; year++) {
        const seasonData = finalData.filter(season => season.Year === year);
        
        if (seasonData.length > 0) {
          // Find highest and lowest PF (Points For) in this season
          const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
          const minPF = Math.min(...seasonData.map(s => s.PF || 0));
          
          // Check if this manager had the highest PF
          const managerSeason = seasonData.find(s => s.Owner === manager);
          if (managerSeason && managerSeason.PF === maxPF) {
            scoreChamp++;
          }
          
          // Check if this manager had the lowest PF
          if (managerSeason && managerSeason.PF === minPF) {
            scoreLoser++;
          }
        }
      }

      // FIXED: Calculate total legacy score exactly like LegacyScoreFactors
      const fullLegacyScore = regWins * 5 + regLosses * -5 +
                              weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +
                              weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +
                              weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +
                              weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +
                              weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +
                              weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +
                              playoffWins * 10 +
                              standingsFinishes[0] * 125 + standingsFinishes[1] * 75 + standingsFinishes[2] * 60 +
                              standingsFinishes[3] * 40 + standingsFinishes[4] * 30 + standingsFinishes[5] * 15 +
                              standingsFinishes[6] * -15 + standingsFinishes[7] * -30 + standingsFinishes[8] * -40 +
                              standingsFinishes[9] * -50 + standingsFinishes[10] * -60 + standingsFinishes[11] * -80 +
                              scoreChamp * 30 + scoreLoser * -30;

      legacyScores.push({
        manager,
        totalScore: Math.round(fullLegacyScore),
        scoreChange: scoreChange, // Make sure scoreChange is passed through
        regWins,
        regLosses,
        playoffWins,
        weeklyPositions,
        standingsFinishes: standingsFinishes, // Use 0-indexed array like LegacyScoreFactors
        scoreChamp,
        scoreLoser,
        seasons: managerSeasons.length
      });
    });

    return legacyScores.sort((a, b) => b.totalScore - a.totalScore);
  };

  // Update legacy data when filters change
  useEffect(() => {
    if (h2hData.length && finalData.length) {
      const scores = calculateLegacyScores();
      setLegacyData(scores);
      
      // Debug output for verification
      if (scores.length > 0) {
        const kevin = scores.find(s => s.manager === 'Kevin');
        if (kevin) {
          console.log('=== KEVIN LEGACY SCORE DEBUG (COMPREHENSIVE LEGACYSCORE.JS FIXED) ===');
          console.log('Total calculated score:', kevin.totalScore);
          console.log('Weekly positions:', kevin.weeklyPositions);
          console.log('2nd place finishes (FIXED):', kevin.weeklyPositions.second);
          console.log('11th place finishes:', kevin.weeklyPositions.eleventh);
          console.log('Should be 23 for 2nd place, 7 for 11th place');
        }
      }
    }
  }, [h2hData, finalData, yearRange]);

  // Handle manager selection for detailed breakdown
  const handleManagerSelect = (manager) => {
    const managerData = legacyData.find(data => data.manager === manager);
    const managerRank = legacyData.findIndex(data => data.manager === manager) + 1;
    
    // Calculate mostRecentWeek for the changeDrivers calculation
    const allWeeks = h2hData.map(game => ({
      year: game.Year,
      week: game.Week,
      leagueWeek: game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)
    }));
    const mostRecentWeek = Math.max(...allWeeks.map(w => w.leagueWeek));
    
    const changeDrivers = calculateChangeDrivers(manager, mostRecentWeek);
    
    setSelectedManager(manager);
    setIndividualBreakdown({ ...managerData, rank: managerRank, changeDrivers });
    setShowBreakdown(true);
  };

  // Calculate what drove the most recent week-over-week change
  const calculateChangeDrivers = (manager, mostRecentWeek) => {
    if (!h2hData.length || !finalData.length) return [];

    const drivers = [];

    // Find games from the most recent week for this manager
    const mostRecentWeekGames = h2hData.filter(game => 
      game.Manager1 === manager && 
      (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek
    );

    let weeklyPerformanceChange = 0;
    let standingsChange = 0;
    let scoringChampChange = 0;
    let playoffChange = 0;
    let regularSeasonChange = 0;

    // 1. REGULAR SEASON WINS/LOSSES from this week
    const regSeasonGames = mostRecentWeekGames.filter(game => game.Type === 'Regular Season');
    regSeasonGames.forEach(game => {
      if (game.IsWin) regularSeasonChange += 5;
      if (game.IsLoss) regularSeasonChange -= 5;
    });

    // 2. WEEKLY SCORING POSITIONS from this week
    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      
      if (winsThisWeek === 11) weeklyPerformanceChange += 15;
      else if (winsThisWeek === 10) weeklyPerformanceChange += 5;
      else if (winsThisWeek === 9) weeklyPerformanceChange += 4;
      else if (winsThisWeek === 8) weeklyPerformanceChange += 3;
      else if (winsThisWeek === 7) weeklyPerformanceChange += 2;
      else if (winsThisWeek === 6) weeklyPerformanceChange += 1;
      else if (winsThisWeek === 5) weeklyPerformanceChange -= 1;
      else if (winsThisWeek === 4) weeklyPerformanceChange -= 2;
      else if (winsThisWeek === 3) weeklyPerformanceChange -= 3;
      else if (winsThisWeek === 2) weeklyPerformanceChange -= 4;
      else if (winsThisWeek === 1) weeklyPerformanceChange -= 5;
      else if (winsThisWeek === 0) weeklyPerformanceChange -= 15;
    });

    // 3. PLAYOFF WINS from this week
    const playoffGames = mostRecentWeekGames.filter(game => game.Type === 'Playoffs');
    playoffGames.forEach(game => {
      if (game.IsWin) playoffChange += 10;
    });

    // 4. END OF SEASON FACTORS (if this is Week 17)
    const isSeasonEnd = mostRecentWeekGames.some(game => game.Week === 17) || 
                       h2hData.some(game => 
                         (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek && 
                         game.Week === 17
                       );
    
    if (isSeasonEnd) {
      // Find what season just ended
      const endedSeasonYear = h2hData.find(game => 
        (game.LeagueWeek || ((game.Year - 2012) * 17 + game.Week)) === mostRecentWeek && 
        game.Week === 17
      )?.Year;
      
      if (endedSeasonYear) {
        // FINAL STANDINGS BONUS/PENALTY
        const managerSeason = finalData.find(season => 
          season.Owner === manager && season.Year === endedSeasonYear
        );
        
        if (managerSeason && managerSeason.Standings >= 1 && managerSeason.Standings <= 12) {
          const standingsPoints = [125, 75, 60, 40, 30, 15, -15, -30, -40, -50, -60, -80][managerSeason.Standings - 1];
          standingsChange += standingsPoints;
        }

        // SCORING CHAMPION/LOSER BONUS/PENALTY
        const seasonData = finalData.filter(season => season.Year === endedSeasonYear);
        if (seasonData.length > 0) {
          const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
          const minPF = Math.min(...seasonData.map(s => s.PF || 0));
          
          const managerPF = managerSeason?.PF || 0;
          if (managerPF === maxPF) scoringChampChange += 30;
          if (managerPF === minPF) scoringChampChange -= 30;
        }
      }
    }

    // Add drivers for each category that had changes
    if (regularSeasonChange !== 0) {
      drivers.push({
        category: 'Regular Season Performance',
        change: regularSeasonChange,
        description: `Head-to-head record impact: ${regularSeasonChange > 0 ? '+' : ''}${regularSeasonChange} points`,
        type: regularSeasonChange > 0 ? 'positive' : 'negative',
        points: regularSeasonChange
      });
    }

    if (weeklyPerformanceChange !== 0) {
      drivers.push({
        category: 'Weekly Scoring Performance',
        change: weeklyPerformanceChange,
        description: `Weekly finish position impact: ${weeklyPerformanceChange > 0 ? '+' : ''}${weeklyPerformanceChange} points`,
        type: weeklyPerformanceChange > 0 ? 'positive' : 'negative',
        points: weeklyPerformanceChange
      });
    }

    if (playoffChange !== 0) {
      drivers.push({
        category: 'Playoff Performance',
        change: playoffChange,
        description: `Playoff games impact: ${playoffChange > 0 ? '+' : ''}${playoffChange} points`,
        type: playoffChange > 0 ? 'positive' : 'negative',
        points: playoffChange
      });
    }

    if (standingsChange !== 0) {
      drivers.push({
        category: 'Season Finish',
        change: standingsChange,
        description: `Final standings position: ${standingsChange > 0 ? '+' : ''}${standingsChange} points`,
        type: standingsChange > 0 ? 'positive' : 'negative',
        points: standingsChange
      });
    }

    if (scoringChampChange !== 0) {
      drivers.push({
        category: 'Scoring Championship',
        change: scoringChampChange,
        description: `Annual scoring race: ${scoringChampChange > 0 ? '+' : ''}${scoringChampChange} points`,
        type: scoringChampChange > 0 ? 'positive' : 'negative',
        points: scoringChampChange
      });
    }

    return drivers;
  };

  // Helper function to calculate total score from breakdown - FIXED to match LegacyScoreFactors
  const calculateTotalScore = (breakdown) => {
    return breakdown.regWins * 5 + breakdown.regLosses * -5 +
           breakdown.weeklyPositions.first * 15 + breakdown.weeklyPositions.twelfth * -15 +
           breakdown.weeklyPositions.second * 5 + breakdown.weeklyPositions.eleventh * -5 +
           breakdown.weeklyPositions.third * 4 + breakdown.weeklyPositions.tenth * -4 +
           breakdown.weeklyPositions.fourth * 3 + breakdown.weeklyPositions.ninth * -3 +
           breakdown.weeklyPositions.fifth * 2 + breakdown.weeklyPositions.eighth * -2 +
           breakdown.weeklyPositions.sixth * 1 + breakdown.weeklyPositions.seventh * -1 +
           breakdown.playoffWins * 10 +
           breakdown.standingsFinishes[0] * 125 + breakdown.standingsFinishes[1] * 75 + breakdown.standingsFinishes[2] * 60 +
           breakdown.standingsFinishes[3] * 40 + breakdown.standingsFinishes[4] * 30 + breakdown.standingsFinishes[5] * 15 +
           breakdown.standingsFinishes[6] * -15 + breakdown.standingsFinishes[7] * -30 + breakdown.standingsFinishes[8] * -40 +
           breakdown.standingsFinishes[9] * -50 + breakdown.standingsFinishes[10] * -60 + breakdown.standingsFinishes[11] * -80 +
           breakdown.scoreChamp * 30 + breakdown.scoreLoser * -30;
  };

  // Helper function to get change indicator icon
  const getScoreChangeIcon = (change) => {
    if (change > 0) return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <ArrowDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  // Helper function to get ordinal suffix
  const getOrdinal = (num) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = num % 100;
    return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
  };

  // Mobile manager card component with enhanced design
  const MobileManagerCard = ({ manager, index }) => {
    const isExpanded = expandedManager === manager.manager;
    const isTopThree = index < 3;
    const images = getManagerImage(manager.manager);

    const toggleExpanded = () => {
      setExpandedManager(isExpanded ? null : manager.manager);
    };

    // Get position styling
    const getPositionStyling = (position) => {
      switch(position) {
        case 0: return { 
          bg: 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600', 
          text: 'text-white', 
          icon: Crown, 
          badge: '👑',
          border: 'border-yellow-300',
          glow: 'shadow-yellow-200/50'
        };
        case 1: return { 
          bg: 'bg-gradient-to-r from-gray-300 via-gray-400 to-gray-500', 
          text: 'text-white', 
          icon: Medal, 
          badge: '🥈',
          border: 'border-gray-300',
          glow: 'shadow-gray-200/50'
        };
        case 2: return { 
          bg: 'bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600', 
          text: 'text-white', 
          icon: Award, 
          badge: '🥉',
          border: 'border-orange-300',
          glow: 'shadow-orange-200/50'
        };
        default: return { 
          bg: 'bg-gradient-to-r from-slate-100 via-slate-200 to-slate-300', 
          text: 'text-slate-700', 
          icon: Target, 
          badge: `#${position + 1}`,
          border: 'border-slate-200',
          glow: 'shadow-slate-200/30'
        };
      }
    };

    const styling = getPositionStyling(index);
    const PositionIcon = styling.icon;

    return (
      <div className={`rounded-2xl overflow-hidden shadow-lg mb-4 transition-all duration-300 transform ${
        isExpanded ? 'scale-[1.02]' : 'scale-100'
      } ${isTopThree ? `border-2 ${styling.border} ${styling.glow} shadow-xl` : 'border border-gray-200'}`}>
        {/* Card Header */}
        <div 
          className={`${styling.bg} p-4 cursor-pointer relative overflow-hidden`}
          onClick={toggleExpanded}
        >
          {/* Background pattern for top 3 */}
          {isTopThree && (
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white transform translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white transform -translate-x-12 translate-y-12"></div>
            </div>
          )}
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center space-x-4">
              {/* Position Badge */}
              <div className="relative">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                  <span className={`text-xl font-bold ${styling.text}`}>{styling.badge}</span>
                </div>
                {isTopThree && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <PositionIcon className="h-3 w-3 text-yellow-600" />
                  </div>
                )}
              </div>
              
              {/* Manager Photo */}
              <div className="w-14 h-14 rounded-full overflow-hidden border-3 border-white/50 shadow-lg bg-white/20 backdrop-blur-sm">
                <img
                  src={images.main}
                  alt={`${manager.manager} profile`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = images.fallback;
                    e.target.onerror = (fallbackEvent) => {
                      fallbackEvent.target.style.display = 'none';
                      fallbackEvent.target.nextSibling.style.display = 'flex';
                    };
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center hidden">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
              
              {/* Manager Info */}
              <div>
                <h3 className={`text-xl font-bold ${styling.text} drop-shadow-sm`}>
                  {manager.manager}
                </h3>
                <p className={`text-sm ${styling.text} opacity-90`}>
                  {manager.regWins}W-{manager.regLosses}L • {manager.seasons} seasons
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Score Change Indicator */}
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  {getScoreChangeIcon(manager.scoreChange)}
                  <span className={`text-xs font-medium ${
                    manager.scoreChange > 0 ? 'text-green-100' : 
                    manager.scoreChange < 0 ? 'text-red-100' : 
                    'text-gray-300'
                  }`}>
                    {manager.scoreChange > 0 ? `+${manager.scoreChange}` :
                     manager.scoreChange < 0 ? manager.scoreChange : '—'}
                  </span>
                </div>
                <div className={`text-xs ${styling.text} opacity-70`}>Change</div>
              </div>
              
              {/* Total Score */}
              <div className="text-right">
                <div className={`text-2xl font-black ${styling.text} drop-shadow-lg`}>
                  {manager.totalScore}
                </div>
                <div className={`text-xs ${styling.text} opacity-80 font-medium`}>
                  Legacy Score
                </div>
              </div>
              
              {/* Expand Icon */}
              <div className={`${styling.text} opacity-80`}>
                {isExpanded ? 
                  <ChevronUp className="h-5 w-5" /> : 
                  <ChevronDown className="h-5 w-5" />
                }
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="bg-white p-4 space-y-4">
            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-3 border border-blue-200">
                <div className="text-blue-600 text-xs font-semibold uppercase tracking-wide mb-1">
                  Championships
                </div>
                <div className="text-blue-900 text-lg font-bold">
                  {manager.standingsFinishes[0]}
                  {manager.standingsFinishes[0] > 0 && (
                    <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                      🏆
                    </span>
                  )}
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-3 border border-green-200">
                <div className="text-green-600 text-xs font-semibold uppercase tracking-wide mb-1">
                  Playoff Wins
                </div>
                <div className="text-green-900 text-lg font-bold">
                  {manager.playoffWins}
                  {manager.playoffWins > 0 && (
                    <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">
                      ⚡
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Performance Highlights */}
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-gray-700 border-b border-gray-200 pb-1">
                Notable Achievements
              </h4>
              
              {/* Top achievements */}
              <div className="grid grid-cols-2 gap-2">
                {manager.standingsFinishes[0] > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold">{manager.standingsFinishes[0]}</div>
                    <div className="text-xs">Championships</div>
                  </div>
                )}
                {manager.standingsFinishes[1] > 0 && (
                  <div className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold">{manager.standingsFinishes[1]}</div>
                    <div className="text-xs">Runner-up</div>
                  </div>
                )}
                {manager.scoreChamp > 0 && (
                  <div className="bg-purple-50 border border-purple-200 text-purple-800 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold">{manager.scoreChamp}</div>
                    <div className="text-xs">Scoring Champ</div>
                  </div>
                )}
                {manager.scoreLoser > 0 && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold">{manager.scoreLoser}</div>
                    <div className="text-xs">Lowest Scorer</div>
                  </div>
                )}
              </div>
            </div>

            {/* View Details Button */}
            <button 
              className="w-full mt-3 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center space-x-2"
              onClick={(e) => {
                e.stopPropagation();
                handleManagerSelect(manager.manager);
              }}
            >
              <Zap className="h-4 w-4" />
              <span>View Complete Breakdown</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Add the banner even during loading */}
        <LegacyScoreBanner />
        
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="flex items-center justify-center h-48 sm:h-64">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-base sm:text-lg text-gray-600">Loading legacy scores...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Add the banner even during error state */}
        <LegacyScoreBanner />
        
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-red-800 font-semibold">Error loading legacy data</h3>
            <p className="text-red-600 mt-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!legacyData.length) {
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Add the banner even when no data */}
        <LegacyScoreBanner />
        
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <p className="text-gray-600">No legacy data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Legacy Score Banner */}
      <LegacyScoreBanner />

      {/* Year Range Filter and Main Content */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Year Range Filter - Mobile optimized */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className={`flex items-center ${isMobile ? 'flex-col space-y-3' : 'space-x-6'}`}>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
              <span className="font-medium text-gray-700 text-sm sm:text-base">
                {isMobile ? 'Years:' : 'Year Range:'}
              </span>
            </div>
            <div className={`${isMobile ? 'w-full' : 'flex-1 max-w-md'}`}>
              <div className="flex items-center space-x-3 sm:space-x-4">
                <span className="text-sm font-medium text-gray-600">{yearRange[0]}</span>
                <div className="flex-1 relative">
                  <div className="range-slider-container">
                    <div 
                      className="range-slider-track"
                      style={{
                        left: `${((yearRange[0] - FIRST_SEASON) / Math.max(1, latestSeason - FIRST_SEASON)) * 100}%`,
                        width: `${((yearRange[1] - yearRange[0]) / Math.max(1, latestSeason - FIRST_SEASON)) * 100}%`
                      }}
                    ></div>
                    <input
                      type="range"
                      min={FIRST_SEASON}
                      max={latestSeason}
                      value={yearRange[0]}
                      onChange={(e) => setRange([parseInt(e.target.value), yearRange[1]])}
                      className="range-slider"
                      style={{ zIndex: 2 }}
                    />
                    <input
                      type="range"
                      min={FIRST_SEASON}
                      max={latestSeason}
                      value={yearRange[1]}
                      onChange={(e) => setRange([yearRange[0], parseInt(e.target.value)])}
                      className="range-slider"
                      style={{ zIndex: 3 }}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-600">{yearRange[1]}</span>
              </div>
            </div>
          </div>
          {yearRange[1] > finalSeason && (
            <p className="mt-3 text-xs text-gray-500">
              {finalSeason + 1} is in progress — weekly all-play and win/loss points are counted as they
              happen. Final-standings points are awarded when the season ends.
            </p>
          )}
        </div>

{/* Top 3 Podium - Mobile optimized */}
{legacyData.length >= 3 && (
          <div className="p-4 sm:p-8">
            <div className={`grid gap-4 sm:gap-6 max-w-4xl mx-auto ${
              isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'
            }`}>
              {/* Mobile: Show 1st place first, then 2nd and 3rd */}
              {isMobile ? (
                <>
                  {/* 1st Place - Mobile */}
                  <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                       onClick={() => handleManagerSelect(legacyData[0].manager)}>
                    <div className="bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-2xl shadow-xl p-4 text-center border-4 border-yellow-400 relative overflow-hidden">
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-20">
                        <Crown className="h-6 w-6 text-yellow-600" />
                      </div>
                      
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
                        style={{
                          backgroundImage: `url('/${legacyData[0].manager.toLowerCase()}happy.png')`,
                          filter: 'sepia(30%) saturate(150%)'
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-yellow-900/20 to-transparent"></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-3 mt-1">
                          <Trophy className="h-16 w-16 text-yellow-700" />
                        </div>
                        <h3 className="text-xl font-bold text-yellow-900 mb-2">{legacyData[0].manager}</h3>
                        <div className="text-3xl font-bold text-yellow-800 mb-2">{legacyData[0].totalScore}</div>
                        <div className="text-sm text-yellow-700 mb-3 font-medium">Champion</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-yellow-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-yellow-900">{legacyData[0].regWins}W</div>
                            <div className="text-yellow-700">Reg Wins</div>
                          </div>
                          <div className="bg-yellow-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-yellow-900">{legacyData[0].standingsFinishes[0]}</div>
                            <div className="text-yellow-700">Titles</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2nd and 3rd Place - Mobile Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* 2nd Place */}
                    <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                         onClick={() => handleManagerSelect(legacyData[1].manager)}>
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl p-3 text-center border-4 border-gray-300 relative overflow-hidden">
                        {/* Background Image */}
                        <div 
                          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                          style={{
                            backgroundImage: `url('/${legacyData[1].manager.toLowerCase()}happy.png')`,
                            filter: 'grayscale(20%)'
                          }}
                        ></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-800/30 to-transparent"></div>
                        
                        {/* Content */}
                        <div className="relative z-10">
                          <div className="flex items-center justify-center mb-3">
                            <Medal className="h-12 w-12 text-gray-500" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-800 mb-2">{legacyData[1].manager}</h3>
                          <div className="text-2xl font-bold text-gray-600 mb-2">{legacyData[1].totalScore}</div>
                          <div className="text-xs text-gray-500 mb-3">2nd Place</div>
                          <div className="grid grid-cols-1 gap-1 text-xs">
                            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-1">
                              <div className="font-semibold text-gray-900">{legacyData[1].regWins}W</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 3rd Place */}
                    <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                         onClick={() => handleManagerSelect(legacyData[2].manager)}>
                      <div className="bg-gradient-to-br from-orange-200 to-orange-300 rounded-2xl shadow-xl p-3 text-center border-4 border-orange-400 relative overflow-hidden">
                        {/* Background Image */}
                        <div 
                          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                          style={{
                            backgroundImage: `url('/${legacyData[2].manager.toLowerCase()}happy.png')`,
                            filter: 'sepia(20%) saturate(120%)'
                          }}
                        ></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-orange-800/20 to-transparent"></div>
                        
                        {/* Content */}
                        <div className="relative z-10">
                          <div className="flex items-center justify-center mb-3">
                            <Award className="h-12 w-12 text-orange-600" />
                          </div>
                          <h3 className="text-lg font-bold text-orange-800 mb-2">{legacyData[2].manager}</h3>
                          <div className="text-2xl font-bold text-orange-700 mb-2">{legacyData[2].totalScore}</div>
                          <div className="text-xs text-orange-600 mb-3">3rd Place</div>
                          <div className="grid grid-cols-1 gap-1 text-xs">
                            <div className="bg-orange-100/90 backdrop-blur-sm rounded-lg p-1">
                              <div className="font-semibold text-orange-900">{legacyData[2].regWins}W</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Desktop layout - preserved */}
                  {/* 2nd Place */}
                  <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                       onClick={() => handleManagerSelect(legacyData[1].manager)}>
                    <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl p-6 text-center border-4 border-gray-300 relative overflow-hidden">
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                        style={{
                          backgroundImage: `url('/${legacyData[1].manager.toLowerCase()}happy.png')`,
                          filter: 'grayscale(20%)'
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-800/30 to-transparent"></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-4">
                          <Medal className="h-16 w-16 text-gray-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">{legacyData[1].manager}</h3>
                        <div className="text-3xl font-bold text-gray-600 mb-2">{legacyData[1].totalScore}</div>
                        <div className="text-sm text-gray-500 mb-4">2nd Place</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-gray-900">{legacyData[1].regWins}W</div>
                            <div className="text-gray-500">Reg Wins</div>
                          </div>
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-gray-900">{legacyData[1].standingsFinishes[0]}</div>
                            <div className="text-gray-500">Titles</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 1st Place */}
                  <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                       onClick={() => handleManagerSelect(legacyData[0].manager)}>
                    <div className="bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-2xl shadow-xl p-6 text-center border-4 border-yellow-400 relative overflow-hidden">
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                        <Crown className="h-8 w-8 text-yellow-600" />
                      </div>
                      
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
                        style={{
                          backgroundImage: `url('/${legacyData[0].manager.toLowerCase()}happy.png')`,
                          filter: 'sepia(30%) saturate(150%)'
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-yellow-900/20 to-transparent"></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-4 mt-2">
                          <Trophy className="h-20 w-20 text-yellow-700" />
                        </div>
                        <h3 className="text-2xl font-bold text-yellow-900 mb-2">{legacyData[0].manager}</h3>
                        <div className="text-4xl font-bold text-yellow-800 mb-2">{legacyData[0].totalScore}</div>
                        <div className="text-sm text-yellow-700 mb-4 font-medium">Champion</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-yellow-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-yellow-900">{legacyData[0].regWins}W</div>
                            <div className="text-yellow-700">Reg Wins</div>
                          </div>
                          <div className="bg-yellow-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-yellow-900">{legacyData[0].standingsFinishes[0]}</div>
                            <div className="text-yellow-700">Titles</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className="transform hover:scale-105 transition-all duration-300 cursor-pointer"
                       onClick={() => handleManagerSelect(legacyData[2].manager)}>
                    <div className="bg-gradient-to-br from-orange-200 to-orange-300 rounded-2xl shadow-xl p-6 text-center border-4 border-orange-400 relative overflow-hidden">
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                        style={{
                          backgroundImage: `url('/${legacyData[2].manager.toLowerCase()}happy.png')`,
                          filter: 'sepia(20%) saturate(120%)'
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-orange-800/20 to-transparent"></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-4">
                          <Award className="h-16 w-16 text-orange-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-orange-800 mb-2">{legacyData[2].manager}</h3>
                        <div className="text-3xl font-bold text-orange-700 mb-2">{legacyData[2].totalScore}</div>
                        <div className="text-sm text-orange-600 mb-4">3rd Place</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-orange-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-orange-900">{legacyData[2].regWins}W</div>
                            <div className="text-orange-700">Reg Wins</div>
                          </div>
                          <div className="bg-orange-100/90 backdrop-blur-sm rounded-lg p-2">
                            <div className="font-semibold text-orange-900">{legacyData[2].standingsFinishes[0]}</div>
                            <div className="text-orange-700">Titles</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Complete Rankings Table - Mobile optimized with enhanced design */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 px-4 sm:px-6 py-4 sm:py-6 relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white transform translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white transform -translate-x-12 translate-y-12"></div>
            </div>
            
            <div className="flex items-center justify-between relative z-10">
              <div>
                <h2 className={`font-bold mb-1 sm:mb-2 text-white drop-shadow-lg ${
                  isMobile ? 'text-xl' : 'text-2xl'
                }`}>
                  {isMobile ? 'Legacy Rankings' : 'Complete Legacy Rankings'}
                </h2>
                <p className={`text-purple-100 ${isMobile ? 'text-sm' : 'text-base'}`}>
                  Complete performance standings
                </p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 shadow-lg">
                <BarChart3 className={`text-white ${isMobile ? 'h-6 w-6' : 'h-8 w-8'}`} />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            {isMobile ? (
              // Mobile: Enhanced Card layout
              <div className="p-4 space-y-4">
                {legacyData.map((manager, index) => (
                  <MobileManagerCard key={manager.manager} manager={manager} index={index} />
                ))}
              </div>
            ) : (
              // Desktop: Enhanced Table layout
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Legacy Score</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Record</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Championships</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Playoff Wins</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Seasons</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {legacyData.map((manager, index) => {
                    const isTopThree = index < 3;
                    const images = getManagerImage(manager.manager);
                    
                    return (
                      <tr key={manager.manager} className={`hover:bg-gray-50 transition-all duration-200 ${
                        isTopThree ? 'bg-gradient-to-r from-yellow-50/30 to-orange-50/30' : ''
                      }`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full text-sm font-bold text-white shadow-lg ${
                              index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 
                              index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500' : 
                              index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 
                              'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-700'
                            }`}>
                              {index === 0 ? '👑' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm">
                              <img
                                src={images.main}
                                alt={`${manager.manager} profile`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = images.fallback;
                                  e.target.onerror = (fallbackEvent) => {
                                    fallbackEvent.target.style.display = 'none';
                                    fallbackEvent.target.nextSibling.style.display = 'flex';
                                  };
                                }}
                              />
                              <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center hidden">
                                <User className="h-5 w-5 text-white" />
                              </div>
                            </div>
                            <div className={`text-sm font-bold ${isTopThree ? 'text-gray-900' : 'text-gray-900'}`}>
                              {manager.manager}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className={`text-xl font-bold ${isTopThree ? 'text-purple-700' : 'text-gray-900'}`}>
                            {manager.totalScore}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {manager.scoreChange > 0 && (
                              <>
                                <ArrowUp className="h-4 w-4 text-green-500" />
                                <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                  +{manager.scoreChange}
                                </span>
                              </>
                            )}
                            {manager.scoreChange < 0 && (
                              <>
                                <ArrowDown className="h-4 w-4 text-red-500" />
                                <span className="text-sm font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                                  {manager.scoreChange}
                                </span>
                              </>
                            )}
                            {manager.scoreChange === 0 && (
                              <>
                                <Minus className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-400 bg-gray-50 px-2 py-1 rounded-full">—</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {manager.regWins}W-{manager.regLosses}L
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <span className={`text-lg font-bold ${manager.standingsFinishes[0] > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                              {manager.standingsFinishes[0]}
                            </span>
                            {manager.standingsFinishes[0] > 0 && (
                              <Trophy className="h-4 w-4 text-yellow-500 ml-1" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          <span className="font-medium">{manager.playoffWins}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                          {manager.seasons}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleManagerSelect(manager.manager)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            <Zap className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Individual Manager Breakdown Modal - Mobile optimized */}
        {showBreakdown && individualBreakdown && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
            <div className={`bg-white rounded-2xl shadow-2xl w-full flex flex-col relative overflow-hidden ${
              isMobile ? 'max-h-[95vh] h-full' : 'max-w-4xl max-h-[90vh]'
            }`}>
              {/* Modal Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-45"
                style={{
                  backgroundImage: `url('/${selectedManager.toLowerCase()}main.png')`,
                  filter: 'blur(1px)'
                }}
              ></div>
              
              {/* Fixed Header - Mobile optimized */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl flex-shrink-0 relative overflow-hidden">
                {/* Header Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
                  style={{
                    backgroundImage: `url('/${selectedManager.toLowerCase()}main.png')`,
                    filter: 'sepia(20%) saturate(120%)'
                  }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/40 to-indigo-900/40"></div>
                
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center">
                    {/* Manager Image in Header - Mobile responsive */}
                    <div className={`rounded-full overflow-hidden border-4 border-white/30 shadow-lg bg-white/10 backdrop-blur-sm flex-shrink-0 ${
                      isMobile ? 'w-12 h-12 mr-3' : 'w-16 h-16 mr-4'
                    }`}>
                      <img
                        src={`/${selectedManager.toLowerCase()}main.png`}
                        alt={selectedManager}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center" style={{display: 'none'}}>
                        <Star className={`text-white/70 ${isMobile ? 'h-6 w-6' : 'h-8 w-8'}`} />
                      </div>
                    </div>
                    <div>
                      <h2 className={`font-bold text-white drop-shadow-lg ${
                        isMobile ? 'text-lg' : 'text-2xl'
                      }`}>
                        {isMobile ? selectedManager : `${selectedManager} - Legacy Breakdown`}
                      </h2>
                      <div className="flex items-center mt-1">
                        <div className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-white/20 text-white backdrop-blur-sm border border-white/20">
                          <Trophy className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          {individualBreakdown.totalScore} Points
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBreakdown(false)}
                    className="text-white hover:text-gray-200 transition-colors duration-200 bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-sm"
                  >
                    <X className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                  </button>
                </div>
              </div>

              {/* Scrollable Content - Mobile optimized */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 relative z-10">
                {/* Change Drivers - Mobile optimized */}
                {individualBreakdown.changeDrivers && individualBreakdown.changeDrivers.length > 0 && (
                  <div className="mb-6 sm:mb-8">
                    <h3 className={`font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center ${
                      isMobile ? 'text-base' : 'text-lg'
                    }`}>
                      <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-purple-600" />
                      Recent Changes
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                      {individualBreakdown.changeDrivers.map((driver, index) => (
                        <div key={index} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-purple-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                              <div className={`p-2 rounded-lg ${
                                driver.type === 'positive' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                              }`}>
                                {driver.type === 'positive' ? 
                                  <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" /> : 
                                  <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
                                }
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className={`font-medium text-gray-900 truncate ${
                                  isMobile ? 'text-sm' : 'text-base'
                                }`}>
                                  {driver.category}
                                </div>
                                <div className={`${
                                  driver.type === 'positive' ? 'text-green-600' : 'text-red-600'
                                } ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                  {driver.description}
                                </div>
                              </div>
                            </div>
                            <div className={`font-bold flex-shrink-0 ml-2 ${
                              driver.type === 'positive' ? 'text-green-400' : 'text-red-400'
                            } ${isMobile ? 'text-base' : 'text-lg'}`}>
                              {driver.points > 0 ? '+' : ''}{driver.points}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {individualBreakdown.changeDrivers.length === 0 && (
                      <div className="text-center py-6 sm:py-8">
                        <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">📊</div>
                        <div className="text-gray-300 font-medium text-sm sm:text-base">No changes from previous week</div>
                        <div className="text-gray-400 text-xs sm:text-sm">Legacy score remained the same</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Score Components Grid - Mobile responsive */}
                <div className={`grid gap-4 sm:gap-8 ${
                  isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'
                }`}>
                  {/* Regular Season */}
                  <div className="bg-gradient-to-br from-green-600 via-green-700 to-emerald-800 rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                        <div className="p-2 sm:p-3 bg-white/20 rounded-full backdrop-blur-sm">
                          <Users className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                        </div>
                        <div>
                          <h4 className={`font-bold ${isMobile ? 'text-lg' : 'text-xl'}`}>Regular Season</h4>
                          <p className={`text-green-100 ${isMobile ? 'text-xs' : 'text-sm'}`}>Head-to-Head Performance</p>
                        </div>
                      </div>
                      <div className="space-y-3 sm:space-y-4">
                        <div className="bg-black/20 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/10">
                          <div className="flex justify-between items-center">
                            <span className={`text-green-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                              Wins ({individualBreakdown.regWins})
                            </span>
                            <span className={`font-bold text-green-300 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                              +{individualBreakdown.regWins * 5}
                            </span>
                          </div>
                        </div>
                        <div className="bg-black/20 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/10">
                          <div className="flex justify-between items-center">
                            <span className={`text-green-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                              Losses ({individualBreakdown.regLosses})
                            </span>
                            <span className={`font-bold text-red-300 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                              {individualBreakdown.regLosses * -5}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Standings Finishes */}
                  <div className="bg-gradient-to-br from-yellow-500 via-yellow-600 to-orange-600 rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                        <div className="p-2 sm:p-3 bg-white/20 rounded-full backdrop-blur-sm">
                          <Trophy className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                        </div>
                        <div>
                          <h4 className={`font-bold ${isMobile ? 'text-lg' : 'text-xl'}`}>Season Finishes</h4>
                          <p className={`text-yellow-100 ${isMobile ? 'text-xs' : 'text-sm'}`}>Annual Standings</p>
                        </div>
                      </div>
                      <div className={`space-y-2 overflow-y-auto ${isMobile ? 'max-h-48' : 'max-h-64'}`}>
                        {individualBreakdown.standingsFinishes.map((count, index) => {
                          if (count === 0) return null;
                          const place = index + 1; // Convert back to 1-based for display
                          const points = place === 1 ? 125 : place === 2 ? 75 : place === 3 ? 60 : 
                                        place === 4 ? 40 : place === 5 ? 30 : place === 6 ? 15 :
                                        place === 7 ? -15 : place === 8 ? -30 : place === 9 ? -40 :
                                        place === 10 ? -50 : place === 11 ? -60 : -80;
                          
                          // Find which years this manager finished in this place
                          const yearsInThisPlace = finalData
                            .filter(season => 
                              season.Owner === selectedManager && 
                              season.Standings === place &&
                              season.Year >= yearRange[0] && 
                              season.Year <= yearRange[1]
                            )
                            .map(season => season.Year)
                            .sort((a, b) => b - a); // Sort years descending (most recent first)
                          
                          return (
                            <div key={place} className="bg-black/20 rounded-xl p-2 sm:p-3 backdrop-blur-sm border border-white/10">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-yellow-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                                      {getOrdinal(place)} ({count}x)
                                    </span>
                                    <span className={`font-bold ${points > 0 ? 'text-green-300' : 'text-red-300'} ${
                                      isMobile ? 'text-base' : 'text-lg'
                                    }`}>
                                      {points > 0 ? '+' : ''}{points * count}
                                    </span>
                                  </div>
                                  <div className={`text-yellow-200/80 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                                    {yearsInThisPlace.join(', ')}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Add Scoring Champion/Loser if applicable */}
                        {individualBreakdown.scoreChamp > 0 && (
                          <div className="bg-black/20 rounded-xl p-2 sm:p-3 backdrop-blur-sm border border-white/10">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-yellow-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                                    Scoring Champion ({individualBreakdown.scoreChamp}x)
                                  </span>
                                  <span className={`font-bold text-green-300 ${isMobile ? 'text-base' : 'text-lg'}`}>
                                    +{individualBreakdown.scoreChamp * 30}
                                  </span>
                                </div>
                                <div className={`text-yellow-200/80 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                                  {(() => {
                                    const champYears = [];
                                    for (let year = yearRange[0]; year <= yearRange[1]; year++) {
                                      const seasonData = finalData.filter(season => season.Year === year);
                                      if (seasonData.length > 0) {
                                        const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
                                        const managerSeason = seasonData.find(s => s.Owner === selectedManager);
                                        if (managerSeason && managerSeason.PF === maxPF) {
                                          champYears.push(year);
                                        }
                                      }
                                    }
                                    return champYears.sort((a, b) => b - a).join(', ');
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {individualBreakdown.scoreLoser > 0 && (
                          <div className="bg-black/20 rounded-xl p-2 sm:p-3 backdrop-blur-sm border border-white/10">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-yellow-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                                    Scoring Loser ({individualBreakdown.scoreLoser}x)
                                  </span>
                                  <span className={`font-bold text-red-300 ${isMobile ? 'text-base' : 'text-lg'}`}>
                                    {individualBreakdown.scoreLoser * -30}
                                  </span>
                                </div>
                                <div className={`text-yellow-200/80 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                                  {(() => {
                                    const loserYears = [];
                                    for (let year = yearRange[0]; year <= yearRange[1]; year++) {
                                      const seasonData = finalData.filter(season => season.Year === year);
                                      if (seasonData.length > 0) {
                                        const minPF = Math.min(...seasonData.map(s => s.PF || 0));
                                        const managerSeason = seasonData.find(s => s.Owner === selectedManager);
                                        if (managerSeason && managerSeason.PF === minPF) {
                                          loserYears.push(year);
                                        }
                                      }
                                    }
                                    return loserYears.sort((a, b) => b - a).join(', ');
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Playoff Performance */}
                  <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-800 rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                        <div className="p-2 sm:p-3 bg-white/20 rounded-full backdrop-blur-sm">
                          <Target className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                        </div>
                        <div>
                          <h4 className={`font-bold ${isMobile ? 'text-lg' : 'text-xl'}`}>Playoffs</h4>
                          <p className={`text-blue-100 ${isMobile ? 'text-xs' : 'text-sm'}`}>Postseason Performance</p>
                        </div>
                      </div>
                      <div className="space-y-3 sm:space-y-4">
                        <div className="bg-black/20 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/10">
                          <div className="flex justify-between items-center">
                            <span className={`text-blue-100 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
                              Playoff Wins ({individualBreakdown.playoffWins})
                            </span>
                            <span className={`font-bold text-green-300 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                              +{individualBreakdown.playoffWins * 10}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weekly Scoring Performance - Enhanced and mobile optimized */}
                <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-gray-900 rounded-3xl shadow-2xl p-4 sm:p-8 relative overflow-hidden mt-6 sm:mt-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                  <div className="relative z-10">
                    <div className="flex items-center space-x-3 sm:space-x-4 mb-6 sm:mb-8">
                      <div className="p-3 sm:p-4 bg-white/10 rounded-full backdrop-blur-sm">
                        <BarChart3 className={`text-white ${isMobile ? 'h-6 w-6' : 'h-8 w-8'}`} />
                      </div>
                      <div>
                        <h4 className={`font-bold text-white ${isMobile ? 'text-xl' : 'text-2xl'}`}>Weekly Scoring Performance</h4>
                        <p className={`text-gray-300 ${isMobile ? 'text-xs' : 'text-sm'}`}>Points by weekly finish position</p>
                      </div>
                    </div>
                    <div className="space-y-6 sm:space-y-8">
                      {/* Good Positions (1st-6th) */}
                      <div>
                        <div className="flex items-center mb-4 sm:mb-6">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mr-2 sm:mr-3"></div>
                          <h5 className={`font-semibold text-green-400 ${isMobile ? 'text-base' : 'text-lg'}`}>
                            Top Half Finishes (Positive Points)
                          </h5>
                        </div>
                        <div className={`grid gap-2 sm:gap-4 ${
                          isMobile ? 'grid-cols-3' : 'grid-cols-6'
                        }`}>
                          {(() => {
                            const topPositions = [
                              { key: 'first', position: '1st', multiplier: 15 },
                              { key: 'second', position: '2nd', multiplier: 5 },
                              { key: 'third', position: '3rd', multiplier: 4 },
                              { key: 'fourth', position: '4th', multiplier: 3 },
                              { key: 'fifth', position: '5th', multiplier: 2 },
                              { key: 'sixth', position: '6th', multiplier: 1 }
                            ];

                            return topPositions.map(({ key, position, multiplier }) => {
                              const count = individualBreakdown.weeklyPositions[key] || 0;
                              if (count === 0) return <div key={key} className="invisible"></div>;
                              
                              const totalPoints = count * multiplier;
                              
                              return (
                                <div key={key} className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-2xl p-2 sm:p-4 text-center backdrop-blur-sm border border-green-500/20">
                                  <div className={`font-bold text-green-400 mb-1 sm:mb-2 ${
                                    isMobile ? 'text-sm' : 'text-lg'
                                  }`}>
                                    {position}
                                  </div>
                                  <div className={`text-green-300 font-medium mb-1 ${
                                    isMobile ? 'text-xs' : 'text-sm'
                                  }`}>
                                    {count} times
                                  </div>
                                  <div className={`text-green-200 mb-1 sm:mb-2 ${
                                    isMobile ? 'text-xs' : 'text-xs'
                                  }`}>
                                    ×{multiplier} pts each
                                  </div>
                                  <div className={`font-bold text-green-300 ${
                                    isMobile ? 'text-sm' : 'text-lg'
                                  }`}>
                                    +{totalPoints}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Poor Positions (7th-12th) */}
                      <div>
                        <div className="flex items-center mb-4 sm:mb-6">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-red-400 to-rose-500 rounded-full mr-2 sm:mr-3"></div>
                          <h5 className={`font-semibold text-red-400 ${isMobile ? 'text-base' : 'text-lg'}`}>
                            Bottom Half Finishes (Negative Points)
                          </h5>
                        </div>
                        <div className={`grid gap-2 sm:gap-4 ${
                          isMobile ? 'grid-cols-3' : 'grid-cols-6'
                        }`}>
                          {(() => {
                            const bottomPositions = [
                              { key: 'seventh', position: '7th', multiplier: -1 },
                              { key: 'eighth', position: '8th', multiplier: -2 },
                              { key: 'ninth', position: '9th', multiplier: -3 },
                              { key: 'tenth', position: '10th', multiplier: -4 },
                              { key: 'eleventh', position: '11th', multiplier: -5 },
                              { key: 'twelfth', position: '12th', multiplier: -15 }
                            ];

                            return bottomPositions.map(({ key, position, multiplier }) => {
                              const count = individualBreakdown.weeklyPositions[key] || 0;
                              if (count === 0) return <div key={key} className="invisible"></div>;
                              
                              const totalPoints = count * multiplier;
                              
                              return (
                                <div key={key} className="bg-gradient-to-br from-red-600/20 to-rose-600/20 rounded-2xl p-2 sm:p-4 text-center backdrop-blur-sm border border-red-500/20">
                                  <div className={`font-bold text-red-400 mb-1 sm:mb-2 ${
                                    isMobile ? 'text-sm' : 'text-lg'
                                  }`}>
                                    {position}
                                  </div>
                                  <div className={`text-red-300 font-medium mb-1 ${
                                    isMobile ? 'text-xs' : 'text-sm'
                                  }`}>
                                    {count} times
                                  </div>
                                  <div className={`text-red-200 mb-1 sm:mb-2 ${
                                    isMobile ? 'text-xs' : 'text-xs'
                                  }`}>
                                    ×{multiplier} pts each
                                  </div>
                                  <div className={`font-bold text-red-300 ${
                                    isMobile ? 'text-sm' : 'text-lg'
                                  }`}>
                                    {totalPoints}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Legacy Score Factors Table */}
      <LegacyScoreFactors 
        h2hData={h2hData} 
        finalData={finalData} 
        yearRange={yearRange} 
      />
      
      {/* Legacy Score Over Time Chart */}
      <LegacyScoreOverTime 
        h2hData={h2hData} 
        finalData={finalData} 
        legacyData={legacyByYearData}
        yearRange={yearRange} 
      />

      <LegacyScoreRankOverTime 
        h2hData={h2hData} 
        finalData={finalData} 
        yearRange={yearRange}
      />

      {/* Individual Teams Legacy Chart */}
      <IndividualTeamsLegacyChart2 
        finalData={finalData} 
        h2hData={h2hData}
        yearRange={yearRange} 
      />
    </div>
  );
};

export default LegacyScore;